import { useEffect, useMemo, useRef, useState } from 'react';
import type { GetRecipeResponse as RecipeDetailData, Id, Ingredient, Step } from '@mlc/shared';
import { dureeSecondesAffichee, formatDureeSecondes, formatRelativeDate, formatTemperatureDEtape, joinQuantityUnit, provenanceDeRecette } from '@mlc/shared';
import { adaptRecipeQuantities } from '@mlc/server/src/ingredients/adaptQuantities';
import type { AdaptedQuantity } from '@mlc/server/src/ingredients/adaptQuantities';
import { readStoredTargetServings } from './servingsStorage';
import { avancement, dismissTimer, formatRemaining, startTimer, useTimers } from './timers';
import { StepText } from './StepText';
import { CookLogForm } from './CookLogForm';
import type { IconName } from './ui';
import { Button, Checkbox, FOCUS_RING, Icon, LinkButton, buttonClasses } from './ui';
import { COOK_TEXT_MAX, COOK_TEXT_MIN, useCookTextSize } from './cookTextSize';
import type { VoiceCommandKind } from './voiceCommands';
import { useVoiceCommands } from './voiceCommands';
import { arreterLecture, parler, useLectureVocale } from './speech';
import { pinRecipe, unpinRecipe } from './activeRecipes';
import { paths, withSearchParam } from './router';

export interface CookModeProps {
  detail: RecipeDetailData;
  /** Retour à la fiche recette, sans enregistrer de réalisation (fermeture directe, ou après « Passer »). */
  onExit: () => void;
  /** Une réalisation vient d'être enregistrée (US-10) : la fiche recette doit rafraîchir son état. */
  onCookLogSaved: (detail: RecipeDetailData) => void;
}

// ----------------------------------------------------------------------------
// État de session (US-08 : « l'état persiste si l'application est fermée puis
// rouverte pendant la même session » / « quitter puis revenir restitue
// l'étape en cours »). Même mécanisme que le sélecteur de portions (US-07,
// servingsStorage.ts) : sessionStorage, accès protégé par try/catch.
// ----------------------------------------------------------------------------

interface CookModeState {
  /**
   * Rang de l'étape en cours. **Conservé pour la compatibilité seulement**
   * (US-77) : c'est `stepId` qui fait foi. Une session écrite avant cette story
   * — quelqu'un qui cuisine au moment de la mise à jour — n'a que ce rang, et
   * on doit pouvoir la reprendre.
   */
  stepIndex: number;
  /**
   * Identifiant de l'étape en cours (US-77). L'ordre des étapes se change
   * désormais d'un geste : mémoriser un rang ferait sauter d'une étape à une
   * autre, sur la tablette, pendant la cuisson, sans que rien ne le dise.
   */
  stepId: Id | null;
  checkedStepIds: Id[];
  checkedIngredientIds: Id[];
  /**
   * Horodatage epoch (ms) de début du mode cuisine (US-10), fixé une seule
   * fois à l'entrée dans l'écran et conservé à travers les étapes et les
   * reprises de session — « actual_minutes … mesurée depuis le début du mode
   * cuisine » (AC US-10), pas depuis la dernière étape ni la dernière reprise.
   */
  startedAt: number;
  /**
   * Rappel « la dernière fois » déjà vu pendant cette session de cuisson
   * (US-17). Il se réaffichait à chaque entrée dans le mode cuisine, y
   * compris en revenant à l'étape 5 sur 5 : utile la première fois, gênant la
   * cinquième (audit externe, constat 28). Effacé avec le reste de l'état à la
   * fin réelle de la cuisine, donc réaffiché à la prochaine cuisson.
   */
  reminderSeen: boolean;
}

function cookModeStorageKey(recipeId: string): string {
  return `mlc:mode-cuisine:${recipeId}`;
}

function readCookModeState(recipeId: string): CookModeState | null {
  try {
    const raw = sessionStorage.getItem(cookModeStorageKey(recipeId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<CookModeState>;
    if (typeof parsed.stepIndex !== 'number') return null;
    return {
      stepIndex: parsed.stepIndex,
      stepId: typeof parsed.stepId === 'string' ? parsed.stepId : null,
      checkedStepIds: Array.isArray(parsed.checkedStepIds) ? parsed.checkedStepIds : [],
      checkedIngredientIds: Array.isArray(parsed.checkedIngredientIds) ? parsed.checkedIngredientIds : [],
      startedAt: typeof parsed.startedAt === 'number' ? parsed.startedAt : Date.now(),
      reminderSeen: parsed.reminderSeen === true,
    };
  } catch {
    return null;
  }
}

/**
 * Étape à reprendre à l'ouverture (US-77).
 *
 * L'identifiant d'abord, et seulement s'il désigne encore une étape de la
 * recette : entre deux ouvertures, l'étape a pu être supprimée. À défaut — une
 * session écrite avant cette story, ou une étape disparue — on retombe sur le
 * rang, borné. Repartir de l'étape 1 serait pire que reprendre à peu près : on
 * perdrait le seul repère de quelqu'un qui a les mains dans la pâte.
 */
function etapeAReprendre(steps: readonly Step[], stored: CookModeState | null): Id | null {
  if (steps.length === 0) return null;
  if (stored?.stepId != null && steps.some((step) => step.id === stored.stepId)) return stored.stepId;
  const rang = Math.min(Math.max(stored?.stepIndex ?? 0, 0), steps.length - 1);
  return steps[rang]?.id ?? null;
}

function writeCookModeState(recipeId: string, state: CookModeState): void {
  try {
    sessionStorage.setItem(cookModeStorageKey(recipeId), JSON.stringify(state));
  } catch {
    // Mémorisation en confort : une écriture refusée ne doit pas casser le mode cuisine.
  }
}

/**
 * Efface l'état de session à la fin réelle de la cuisine (réalisation
 * enregistrée ou écran de fin passé) : la prochaine ouverture du mode
 * cuisine pour cette recette doit repartir de la première étape et d'un
 * nouveau `startedAt`, pas reprendre là où la session précédente s'était
 * arrêtée. Un simple « ← Fiche recette » avant d'atteindre l'écran de fin,
 * lui, ne doit pas effacer cet état (US-08 : « quitter puis revenir restitue
 * l'étape en cours ») — cette fonction n'est donc appelée que depuis l'écran
 * de fin ci-dessous, jamais depuis `onExit` du bouton d'en-tête.
 */
function clearCookModeState(recipeId: string): void {
  try {
    sessionStorage.removeItem(cookModeStorageKey(recipeId));
  } catch {
    // Comme ci-dessus : un échec silencieux n'empêche pas de continuer.
  }
}

// ----------------------------------------------------------------------------
// Écran maintenu allumé (§4.3 CLAUDE.md / US-08 : « l'écran ne se met pas en
// veille tant que le mode cuisine est actif »). Screen Wake Lock API, avec
// dégradation silencieuse si absente ou refusée (navigateur, mode privé,
// tablette sans support) — ce n'est pas une exigence bloquante pour cuisiner.
// Un verrou est relâché automatiquement par le navigateur quand l'onglet
// passe en arrière-plan : on le redemande au retour au premier plan.
// ----------------------------------------------------------------------------

interface WakeLockSentinelLike {
  release: () => Promise<void>;
}
interface NavigatorWithWakeLock {
  wakeLock: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
}

function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    async function acquire(): Promise<void> {
      if (!('wakeLock' in navigator)) return;
      try {
        sentinel = await (navigator as unknown as NavigatorWithWakeLock).wakeLock.request('screen');
      } catch {
        // Refusé ou indisponible : le mode cuisine reste utilisable sans lui.
      }
    }

    function handleVisibility(): void {
      if (document.visibilityState === 'visible' && sentinel === null && !cancelled) void acquire();
    }

    void acquire();
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      void sentinel?.release().catch(() => {});
    };
  }, [active]);
}

// ----------------------------------------------------------------------------
// Quantités adaptées (US-07, réutilisées telles quelles ici — même moteur que
// ServingsAdapter dans RecipeDetail.tsx). Sans portions de référence ou sans
// choix mémorisé pour cette recette, les quantités s'affichent telles
// qu'enregistrées : le mode cuisine ne doit jamais bloquer sur une adaptation
// impossible (§3.1 : computeFactor refuse un nombre de portions invalide).
// ----------------------------------------------------------------------------

function useAdaptedIngredients(detail: RecipeDetailData): Map<Id, AdaptedQuantity> {
  return useMemo(() => {
    const map = new Map<Id, AdaptedQuantity>();
    const target = readStoredTargetServings(detail.id) ?? detail.servings;
    let adapted: AdaptedQuantity[] | null = null;
    if (detail.servings !== null && target !== null) {
      const result = adaptRecipeQuantities({
        referenceServings: detail.servings,
        targetServings: target,
        ingredients: detail.ingredients.map((ingredient) => ({
          quantity: ingredient.quantity,
          unit: ingredient.unit,
          scaling: ingredient.scaling,
          name: ingredient.name,
        })),
        steps: [],
      });
      adapted = result.ingredients;
    }
    detail.ingredients.forEach((ingredient, index) => {
      map.set(
        ingredient.id,
        adapted?.[index] ?? { quantity: ingredient.quantity, unit: ingredient.unit, display: null, note: null },
      );
    });
    return map;
  }, [detail]);
}

function toggle<T>(set: ReadonlySet<T>, value: T): Set<T> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

// Boutons larges du mode cuisine (B1, refonte UX/UI passe 3) : voir ui/Button.tsx (variant/size 'large').

/** Distance minimale d'un balayage reconnu (US-31), en pixels. */
const SWIPE_MIN_PX = 60;

/**
 * Libellé du minuteur tiré du **texte** de l'étape et non de son numéro.
 * §3.3 demande des minuteurs « nommés d'après l'étape qui les a lancés » ;
 * « Étape 1 » n'est pas un nom, et deux minuteurs simultanés devenaient
 * indiscernables (audit externe, constat 06).
 */
function stepTimerLabel(step: Step, index: number): string {
  const text = step.instruction.trim().replace(/\s+/g, ' ');
  if (text === '') return `Étape ${index + 1}`;
  return text.length <= 42 ? text : `${text.slice(0, 41).trimEnd()}…`;
}

/**
 * Réglage de taille de texte : deux boutons à cible tactile large, jamais de
 * geste précis (§4 CLAUDE.md). Boutons iconographiques passés par `Button`
 * (`size="icon"`, ui/Button.tsx — audit UX/UI passe 2, F4) : jusqu'ici seul
 * traitement de ce type dans l'application, avec des classes recopiées à la
 * main plutôt qu'issues du design system.
 */
export function TextSizeControl({ size, increase, decrease }: { size: number; increase: () => void; decrease: () => void }): React.JSX.Element {
  return (
    <div role="group" aria-label="Taille du texte" className="flex items-center gap-2">
      <Button
        variant="secondary"
        size="icon"
        onClick={decrease}
        disabled={size <= COOK_TEXT_MIN}
        aria-label="Réduire la taille du texte"
      >
        A<Icon name="moins" size={0.8} />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        onClick={increase}
        disabled={size >= COOK_TEXT_MAX}
        aria-label="Augmenter la taille du texte"
        className="text-xl"
      >
        A<span aria-hidden="true">+</span>
      </Button>
    </div>
  );
}

/**
 * Rappel de la note « la prochaine fois » (US-11 : « re-affiché … quand le
 * mode cuisine se lance »).
 *
 * ### Il ne barre plus le passage (US-58)
 *
 * Il s'affichait en `fixed inset-0` : une fenêtre pleine page, avec un bouton
 * « C'est noté » à toucher **avant de voir la première étape**. Sur toute
 * recette déjà cuisinée — c'est-à-dire celles qu'on relance le plus souvent —
 * lancer la cuisson coûtait donc un geste de plus que sur une recette
 * inconnue, alors que la vague F cherche exactement l'inverse : le chemin le
 * plus court vers la cuisson.
 *
 * Il est maintenant **posé en tête de l'écran, dans le flux**, au-dessus de la
 * première étape. Il se lit sans rien toucher, il se ferme toujours en une
 * seule action, et il s'efface de lui-même dès qu'on avance : une note qui
 * concerne le tour de main du départ n'a rien à dire à l'étape 7.
 */
function NextTimeReminder({
  nextTime,
  cookedAt,
  rating,
  onClose,
}: {
  nextTime: string;
  cookedAt: string | null;
  rating: number | null;
  onClose: () => void;
}): React.JSX.Element {
  // `now` figé au montage : une date relative recalculée à chaque rendu est
  // une fonction impure, et n'apporte rien sur une fenêtre affichée quelques
  // secondes.
  const [now] = useState(() => Date.now());
  const context = [
    cookedAt !== null ? formatRelativeDate(cookedAt, now) : null,
    rating !== null ? `${String(rating)}/5` : null,
  ].filter((part): part is string => part !== null);

  return (
    <div role="status" aria-label="Rappel de la dernière réalisation" className="px-6 pt-4">
      {/* Panneau opaque, et non du texte posé sur un voile (US-17) : à 85 %
          d'opacité, le texte de l'étape se lisait encore derrière celui du
          rappel, et les deux se chevauchaient visiblement (audit externe,
          constat 27). C'est la meilleure idée du produit : elle mérite une
          surface. Elle n'a pas besoin, pour autant, d'occuper l'écran entier. */}
      <div className="mx-auto flex w-full max-w-reading flex-col gap-2 rounded-sheet border border-border bg-surface p-4 shadow-card">
        <p className="text-label">La dernière fois…</p>
        {context.length > 0 && (
          <p className="text-data text-sm text-foreground-muted">{context.join(' · ')}</p>
        )}
        <p className="text-lg leading-snug text-foreground">{nextTime}</p>
        <Button type="button" variant="secondary" size="inline" className="w-fit" onClick={onClose}>
          C’est noté
        </Button>
      </div>
    </div>
  );
}

/**
 * Les mots reconnus, avec le dessin du geste qu'ils déclenchent (US-59, élargi
 * en US-60). Une seule table, lue à la fois par l'aide et par le retour
 * « compris » : deux listes séparées auraient divergé au premier mot ajouté.
 *
 * « étape » n'y figure pas : il ne s'emploie qu'avec un numéro (« étape trois »),
 * et l'aide se lit d'un coup d'œil au-dessus d'une casserole — trois entrées y
 * tiennent, six n'y tiennent plus. Les deux dernières sont donc annoncées en
 * texte, à la suite.
 */
const VOIX_LIBELLES: readonly { command: VoiceCommandKind; mot: string; icone: IconName }[] = [
  { command: 'previous', mot: 'précédent', icone: 'fleche-gauche' },
  { command: 'next', mot: 'suivant', icone: 'fleche-droite' },
  { command: 'timer', mot: 'minuteur', icone: 'minuteur' },
];

const VOIX_PAR_COMMANDE: Record<VoiceCommandKind, { mot: string; icone: IconName }> = {
  previous: { mot: 'précédent', icone: 'fleche-gauche' },
  next: { mot: 'suivant', icone: 'fleche-droite' },
  timer: { mot: 'minuteur', icone: 'minuteur' },
  repeat: { mot: 'répète', icone: 'micro' },
  ingredients: { mot: 'ingrédients', icone: 'panier' },
  step: { mot: 'étape', icone: 'livre' },
};

/**
 * Mode cuisine (US-08) : une seule étape à la fois, en plein écran (recouvre
 * la fiche recette via une surcouche fixe plutôt qu'une nouvelle vue de
 * `App.tsx` — §6 limite à cinq écrans, celui-ci en fait partie mais n'a pas
 * besoin d'un routeur pour être « plein écran »).
 */
export function CookMode({ detail, onExit, onCookLogSaved }: CookModeProps): React.JSX.Element {
  const steps: Step[] = detail.steps;
  /**
   * D'où vient la recette (US-74) — lu deux fois : par l'écran d'une recette
   * sans étape, et par l'écran de fin, qui propose de les noter tant que c'est
   * frais. `null` sur tout le reste de la bibliothèque, où rien ne change.
   */
  const provenanceSansEtapes = provenanceDeRecette(detail);

  /**
   * Phases de la recette (`group_label`), en tranches d'étapes consécutives.
   *
   * Le libellé de groupe s'affichait en `text-sm` au-dessus d'un corps de
   * texte réglé entre 20 et 32 px, dans la colonne de la case à cocher : il se
   * lisait comme la légende de l'étape et non comme le titre de la phase en
   * cours. Il remonte dans la barre du haut, seul endroit qui répond déjà à
   * « où j'en suis », et la barre de progression est découpée en autant de
   * segments — on voit alors qu'il reste deux étapes de pâte avant la crème,
   * ce que « étape 3 sur 11 » ne dit pas.
   *
   * Une recette sans aucun `group_label` donne une tranche unique, soit
   * exactement la barre pleine largeur d'avant.
   */
  const phases = useMemo(() => {
    const result: { label: string | null; start: number; count: number }[] = [];
    steps.forEach((step, index) => {
      const last = result[result.length - 1];
      if (last !== undefined && last.label === step.group_label) last.count += 1;
      else result.push({ label: step.group_label, start: index, count: 1 });
    });
    return result;
  }, [steps]);

  /** Origine du balayage en cours (US-31), ou `null` hors geste. */
  const swipeStartX = useRef<number | null>(null);
  const { timers, now: maintenant } = useTimers();
  const stored = useMemo(() => readCookModeState(detail.id), [detail.id]);
  /**
   * **L'étape en cours est un identifiant ; son rang s'en déduit** (US-77).
   *
   * L'ordre des étapes se change désormais d'un geste, y compris depuis un autre
   * appareil pendant qu'on cuisine. Un rang mémorisé ferait alors sauter d'une
   * étape à une autre au prochain rendu, silencieusement — c'est le seul endroit
   * de cette story qui pouvait casser quelque chose, et le tenir par
   * l'identifiant le referme sans avoir à surveiller quoi que ce soit : la
   * dérivation est refaite à chaque changement de `steps`.
   *
   * `setStepIndex` reste l'interface des quatre appelants (boutons, clavier,
   * balayage, voix) : ils raisonnent en rangs — « suivant », « étape trois » —
   * et ce n'est pas à eux de changer.
   */
  const [stepId, setStepId] = useState<Id | null>(() => etapeAReprendre(steps, stored));
  const stepIndex = useMemo(() => {
    const rang = steps.findIndex((step) => step.id === stepId);
    return rang >= 0 ? rang : 0;
  }, [steps, stepId]);

  function setStepIndex(next: number | ((rang: number) => number)): void {
    setStepId((precedent) => {
      // Le rang est recalculé depuis l'état précédent plutôt que lu dans la
      // fermeture : deux gestes dans le même rendu doivent s'enchaîner.
      const rangPrecedent = Math.max(steps.findIndex((step) => step.id === precedent), 0);
      const voulu = typeof next === 'function' ? next(rangPrecedent) : next;
      const borne = Math.min(Math.max(voulu, 0), Math.max(steps.length - 1, 0));
      return steps[borne]?.id ?? null;
    });
  }
  const [checkedStepIds, setCheckedStepIds] = useState<Set<Id>>(() => new Set(stored?.checkedStepIds ?? []));
  const [checkedIngredientIds, setCheckedIngredientIds] = useState<Set<Id>>(
    () => new Set(stored?.checkedIngredientIds ?? []),
  );
  const [startedAt] = useState(() => stored?.startedAt ?? Date.now());
  // Écran de fin (US-10) : affiché après « Terminer » sur la dernière étape,
  // au lieu de sortir directement du mode cuisine comme avant cette story.
  const [finished, setFinished] = useState(false);
  // `finishedActualMinutes` (plutôt qu'un recalcul à chaque rendu depuis
  // `startedAt`) fige la durée au moment précis du clic sur « Terminer » —
  // recalculer à chaque rendu ferait dériver la valeur préremplie pendant que
  // l'utilisateur remplit le formulaire, et `Date.now()` ne peut de toute
  // façon pas être appelé pendant le rendu (impur, règle des hooks React).
  const [finishedActualMinutes, setFinishedActualMinutes] = useState<number | null>(null);
  // Rappel « la prochaine fois » (US-11) : état purement local, jamais écrit
  // en sessionStorage (cf. commentaire de `NextTimeReminder` ci-dessus).
  const [reminderDismissed, setReminderDismissed] = useState(() => stored?.reminderSeen ?? false);
  /**
   * Tiroir d'ingrédients (US-40, livré en US-58).
   *
   * La liste complète n'était atteignable que **par défaut** : un `<details>`
   * posé dans le corps de l'étape, et seulement quand l'étape n'avait aucun
   * ingrédient apparié. Sur une recette bien appariée — celles qu'on saisit à
   * la main — les ingrédients des *autres* étapes étaient donc inatteignables
   * sans sortir du mode cuisine, revenir à la fiche, puis relancer la cuisson.
   * « Est-ce qu'il me reste des œufs pour la suite ? » coûtait trois écrans.
   *
   * Ouvert d'emblée quand la recette n'a **aucun** appariement étape ↔
   * ingrédient (tout le corpus importé) : c'est le repli d'US-17, qui reste
   * dû — mieux vaut tous les ingrédients que rien.
   */
  const [tiroirOuvert, setTiroirOuvert] = useState(
    () => detail.step_ingredients.length === 0 && detail.ingredients.length > 0,
  );
  const cookTextSize = useCookTextSize();

  useEffect(() => {
    writeCookModeState(detail.id, {
      stepIndex,
      stepId,
      checkedStepIds: [...checkedStepIds],
      checkedIngredientIds: [...checkedIngredientIds],
      startedAt,
      reminderSeen: reminderDismissed,
    });
  }, [detail.id, stepIndex, stepId, checkedStepIds, checkedIngredientIds, startedAt, reminderDismissed]);

  // Le mode cuisine s'utilise avec les mains grasses (§4 CLAUDE.md) : tant
  // qu'il est affiché, l'écran ne doit jamais se verrouiller.
  useWakeLock(true);

  // Recette épinglée tant que le mode cuisine est ouvert (US-35) : c'est ce
  // qui permet de passer d'un plat à son accompagnement sans repasser par la
  // bibliothèque. Dépinglée à la fin de la cuisson, pas à la sortie de
  // l'écran — on quitte souvent le mode cuisine pour aller voir autre chose.
  useEffect(() => {
    pinRecipe(detail.id, detail.title);
  }, [detail.id, detail.title]);

  /**
   * Lecture à voix haute (US-60), **éteinte par défaut** : une application qui
   * se met à parler sans qu'on l'ait demandé est une mauvaise surprise. Quand
   * le réglage est allumé, chaque étape est lue à son arrivée — y compris la
   * première, à l'entrée dans le mode cuisine.
   */
  const lecture = useLectureVocale();
  useEffect(() => {
    if (!lecture.actif || finished) return;
    const step = steps[stepIndex];
    if (step === undefined) return;
    parler(step.instruction);
  }, [lecture.actif, finished, stepIndex, steps]);

  // Quitter la cuisine coupe la parole en cours : une étape lue dans le vide
  // après qu'on a fermé l'écran est exactement ce qu'on ne veut pas.
  useEffect(() => {
    return () => { arreterLecture(); };
  }, []);

  /**
   * Ce que chaque intention déclenche (US-60). Toutes passent par les mêmes
   * fonctions que les boutons : c'est ce qui garantit qu'une commande vocale
   * fait exactement ce que ferait le doigt, sur la dernière étape comme
   * ailleurs.
   */
  const voice = useVoiceCommands((intent) => {
    if (intent.kind === 'next') goNext();
    else if (intent.kind === 'previous') goPrevious();
    else if (intent.kind === 'timer') armerMinuteur(intent.minutes);
    else if (intent.kind === 'repeat') lireEtapeCourante();
    else if (intent.kind === 'ingredients') setTiroirOuvert(true);
    else if (intent.kind === 'step') allerAEtape(intent.numero);
  });

  /**
   * Clavier (US-31) : flèches et barre d'espace. §4 pose que le mode cuisine
   * « s'utilise avec les mains grasses » — passer à l'étape suivante exigeait
   * pourtant de toucher l'écran, et les flèches ne faisaient rien.
   *
   * L'écouteur est posé sur `window` : le mode cuisine est une surcouche
   * plein écran sans champ de saisie, aucun élément n'a donc besoin d'avoir le
   * focus pour que les touches marchent. Il s'efface dès qu'on quitte l'écran
   * de fin, où un formulaire, lui, attend des espaces.
   */
  useEffect(() => {
    if (finished) return;
    function handleKey(event: KeyboardEvent): void {
      // Une touche modifiée appartient au navigateur (onglet suivant, retour
      // arrière…) : on ne la détourne pas.
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      // Un champ a le focus (formulaire de fin, réglage) : la barre d'espace
      // y écrit un espace, elle ne fait pas avancer la recette.
      const target = event.target;
      if (target instanceof HTMLElement && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      if (event.key === 'ArrowRight' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrevious();
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => { window.removeEventListener('keydown', handleKey); };
  });

  const adaptedById = useAdaptedIngredients(detail);
  const ingredientsById = useMemo(() => new Map(detail.ingredients.map((i) => [i.id, i])), [detail.ingredients]);

  // « next_time … re-affiché … au lancement du mode cuisine » (AC US-11) :
  // absent quand la recette n'a jamais été cuisinée, ou que la dernière fois
  // n'avait rien à retenir.
  //
  // `stepIndex === 0` (US-58) : le rappel accompagne le départ. Posé dans le
  // flux, il occuperait sinon le haut de l'écran pendant toute la cuisson,
  // là où le texte de l'étape doit tenir sans défilement.
  const nextTime = detail.latest_cook_log?.next_time ?? null;
  const reminder = nextTime !== null && !reminderDismissed && stepIndex === 0
    ? (
        <NextTimeReminder
          nextTime={nextTime}
          cookedAt={detail.latest_cook_log?.cooked_at ?? null}
          rating={detail.latest_cook_log?.rating ?? null}
          onClose={() => { setReminderDismissed(true); }}
        />
      )
    : null;

  // « actual_minutes est prérempli avec la durée réelle mesurée depuis le
  // début du mode cuisine » (AC US-10) — figée au clic sur « Terminer »
  // (cf. commentaire de `finishedActualMinutes` ci-dessus), pas recalculée
  // pendant l'affichage de l'écran de fin.
  function handleFinish(): void {
    setFinishedActualMinutes(Math.max(0, Math.round((Date.now() - startedAt) / 60_000)));
    setFinished(true);
  }

  /**
   * Avancer et reculer d'une étape (US-31). Une seule paire de fonctions pour
   * toutes les modalités — boutons, clavier, balayage, zones de frappe : c'est
   * ce qui garantit qu'elles font exactement la même chose, y compris sur la
   * dernière étape où « suivant » veut dire « terminer ».
   */
  function goNext(): void {
    if (stepIndex >= steps.length - 1) handleFinish();
    else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  }

  function goPrevious(): void {
    setStepIndex((i) => Math.max(0, i - 1));
  }

  /**
   * « minuteur » à la voix (US-31). Recalcule tout depuis l'état plutôt que
   * de lire les constantes du rendu : la commande arrive de façon asynchrone,
   * et l'étape a pu changer entre-temps.
   *
   * `minutes` (US-60) : durée dictée — « minuteur dix minutes ». Sans elle, on
   * retombe sur la durée de l'étape, saisie ou détectée dans son texte. Une
   * durée dictée passe **outre le minuteur déjà en cours** : la redemander
   * explicitement, c'est vouloir un second temps, pas répéter le premier.
   */
  function armerMinuteur(minutes: number | null): void {
    const step = steps[stepIndex];
    if (step === undefined) return;
    if (minutes === null) {
      const running = timers.find((timer) => timer.source === step.id && timer.fired_at === null);
      // Un minuteur tourne déjà pour cette étape : la commande ne doit pas en
      // créer un second, elle n'a alors rien à faire.
      if (running !== undefined) return;
      const duree = dureeSecondesAffichee(step);
      if (duree === null) return;
      startTimer(stepTimerLabel(step, stepIndex), duree, step.id);
      return;
    }
    // **La dictée reste en minutes, le minuteur est en secondes** (US-98).
    // « minuteur dix minutes » est ce qu'on dit à voix haute ; personne ne
    // dicte « minuteur six cents secondes ». La conversion est ici, au seul
    // endroit où les deux unités se rencontrent, plutôt que dans
    // `voiceIntent.ts`, dont le travail est de comprendre une phrase.
    startTimer(stepTimerLabel(step, stepIndex), minutes * 60, step.id);
  }

  /**
   * « étape trois » (US-60) : on saute directement, sans traverser les étapes
   * intermédiaires à la voix. Un numéro hors bornes ne fait rien plutôt que de
   * ramener au début ou à la fin — se tromper de numéro ne doit pas coûter la
   * position en cours.
   */
  function allerAEtape(numero: number): void {
    if (numero < 1 || numero > steps.length) return;
    setStepIndex(numero - 1);
  }

  /**
   * « répète » (US-60) : relit l'étape en cours à voix haute, que la lecture
   * continue soit activée ou non. C'est le geste des mains dans la pâte, quand
   * on n'a pas vu l'écran passer à l'étape suivante.
   */
  function lireEtapeCourante(): void {
    const step = steps[stepIndex];
    if (step === undefined) return;
    parler(step.instruction);
  }

  function handleCookLogSaved(updated: RecipeDetailData): void {
    clearCookModeState(detail.id);
    unpinRecipe(detail.id);
    onCookLogSaved(updated);
    onExit();
  }

  function handleCookLogDismissed(): void {
    clearCookModeState(detail.id);
    unpinRecipe(detail.id);
    onExit();
  }

  if (finished) {
    return (
      <div
        role="dialog"
        aria-label={`Mode cuisine : ${detail.title}`}
        className="fixed inset-0 z-50 flex flex-col bg-background"
      >
        <header className="border-b border-border p-4">
          <p className="text-heading-2">Comment ça s’est passé ?</p>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          <CookLogForm
            recipeId={detail.id}
            prefilledActualMinutes={finishedActualMinutes}
            dismissLabel="Passer"
            // US-74 : seulement pour les recettes dont le procédé est ailleurs.
            // `steps.length === 0` **et** un lien : une recette sans étape et
            // sans source est une saisie inachevée, pas une recette dont le
            // procédé est dans une vidéo — la proposition n'aurait pas de sens.
            lienEtapes={
              steps.length === 0 && detail.source_url !== null
                ? withSearchParam(paths.editRecipe(detail.id), 'section', 'etapes')
                : null
            }
            onSaved={handleCookLogSaved}
            onDismiss={handleCookLogDismissed}
          />
        </main>
      </div>
    );
  }

  /**
   * **Une recette sans étape entre quand même en mode cuisine** (US-74).
   *
   * C'est la moitié de ce que le sas rapporte : une légende de Reel donne les
   * ingrédients, la vidéo garde les gestes (§C4 du plan de vague G). Cet écran
   * ne disait qu'une phrase — « Cette recette n'a pas encore d'étapes
   * enregistrées » — et laissait donc sans rien, en cuisine, celui qui venait
   * précisément d'ouvrir le mode cuisine.
   *
   * Il porte désormais les trois choses qui servent à ce moment-là : les
   * **ingrédients adaptés** aux portions choisies, le **lien vers la vidéo**, et
   * de quoi **terminer** — sans quoi l'écran de fin, et sa proposition de noter
   * les étapes pendant que c'est frais, serait inatteignable exactement sur les
   * recettes qui en ont besoin.
   *
   * Toutes les cibles sont à `large`, donc `min-h-touch` : 48 px, mains grasses
   * (§4). C'est le mode cuisine, pas un écran de consultation.
   */
  if (steps.length === 0) {
    return (
      <div
        role="dialog"
        aria-label={`Mode cuisine : ${detail.title}`}
        className="fixed inset-0 z-50 flex flex-col gap-6 overflow-y-auto bg-background p-6"
        style={{ fontSize: cookTextSize.size }}
      >
        <Button type="button" variant="secondary" size="large" className="w-fit" onClick={onExit}>
          <Icon name="fleche-gauche" size={0.9} /> Fiche recette
        </Button>
        {reminder}

        <section aria-labelledby="cuisine-sans-etapes" className="flex flex-col gap-3">
          <h2 id="cuisine-sans-etapes" className="text-heading-2">
            {provenanceSansEtapes?.video === true
              ? 'Le procédé est dans la vidéo'
              : 'Cette recette n’a pas encore d’étapes enregistrées'}
          </h2>
          {detail.source_url !== null && (
            <LinkButton
              variant="primary"
              size="large"
              href={detail.source_url}
              target="_blank"
              rel="noreferrer noopener"
              className="w-fit no-underline"
            >
              {provenanceSansEtapes === null
                ? 'Ouvrir la page d’origine'
                : `Revoir sur ${provenanceSansEtapes.nom}`}
            </LinkButton>
          )}
        </section>

        {/* Les ingrédients adaptés aux portions choisies : c'est ce qui reste
            d'utilisable, et c'est ce qu'on lit debout devant le plan de
            travail. Même rendu que le tiroir « Tous les ingrédients », à la
            taille du mode cuisine. */}
        {detail.ingredients.length > 0 && (
          <section aria-labelledby="cuisine-sans-etapes-ingredients" className="flex flex-col gap-3">
            <h2 id="cuisine-sans-etapes-ingredients" className="text-heading-2">Ingrédients</h2>
            <ul aria-label="Tous les ingrédients" className="flex flex-col gap-2">
              {detail.ingredients.map((ingredient) => {
                const adapted = adaptedById.get(ingredient.id);
                return (
                  <li key={ingredient.id} style={{ fontSize: cookTextSize.size * 0.8 }}>
                    {adapted?.display !== null && adapted?.display !== undefined && (
                      <span className="text-data">
                        {joinQuantityUnit(adapted.display, ingredient.unit, adapted.quantity)}{' '}
                      </span>
                    )}
                    {ingredient.name}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        <button
          type="button"
          onClick={handleFinish}
          className={buttonClasses('primary', 'large', 'mt-auto w-full min-h-22')}
        >
          J’ai fini
        </button>
      </div>
    );
  }

  const currentStep = steps[stepIndex];
  if (!currentStep) {
    return (
      <div role="dialog" aria-label={`Mode cuisine : ${detail.title}`} className="fixed inset-0 z-50 bg-background">
        {reminder}
      </div>
    );
  }

  const stepIngredientIds = new Set(
    detail.step_ingredients.filter((pair) => pair.step_id === currentStep.id).map((pair) => pair.ingredient_id),
  );
  const stepIngredients: Ingredient[] = [...stepIngredientIds]
    .map((id) => ingredientsById.get(id))
    .filter((ingredient): ingredient is Ingredient => ingredient !== undefined);

  /**
   * Ce que la recette contient **en plus** de l'étape en cours. Répéter dans
   * « le reste de la recette » les ingrédients déjà listés juste au-dessus
   * ferait lire deux fois la même ligne à trente centimètres d'écart.
   */
  const autresIngredients: Ingredient[] = detail.ingredients.filter(
    (ingredient) => !stepIngredientIds.has(ingredient.id),
  );

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  /** Phase en cours. `phases` couvre toutes les étapes : il y en a toujours une. */
  const currentPhase = phases.find(
    (phase) => stepIndex >= phase.start && stepIndex < phase.start + phase.count,
  ) ?? { label: null, start: 0, count: steps.length };

  /**
   * Ingrédients dont l'étape courante est la **première** apparition. Ailleurs,
   * un ingrédient déjà coché est signalé « déjà préparé » plutôt que présenté
   * comme s'il restait à faire.
   */
  const currentStepFirstUse = new Set(
    stepIngredients
      .filter((ingredient) => {
        const firstStep = steps.find((step) =>
          detail.step_ingredients.some((pair) => pair.step_id === step.id && pair.ingredient_id === ingredient.id),
        );
        return firstStep?.id === currentStep.id;
      })
      .map((ingredient) => ingredient.id),
  );

  // Nom du minuteur (US-09 : « nommés d'après leur étape ») — capturé dans
  // une constante locale plutôt que relu depuis `currentStep` au clic, pour
  // que la narrowing TypeScript de la durée (non nulle) reste valable dans la
  // fermeture `onClick` ci-dessous.
  // Durée saisie si elle existe, sinon détectée dans le texte de l'étape
  // (US-17) : sur une recette importée, la colonne est vide et « Mijoter
  // 30 minutes » n'offrait aucun minuteur (audit externe, constat 05). Une
  // durée vidée à la main reste vide (US-78, D11) — pas de minuteur
  // ressuscité depuis le texte. **En secondes depuis US-98** : « Mixer
  // 20 sec » en arme un, ce que la minute entière interdisait. Rien n'écrit
  // en base ici.
  const dureeSecondes = dureeSecondesAffichee(currentStep);

  const timerLabel = stepTimerLabel(currentStep, stepIndex);

  /** Minuteur déjà lancé pour cette étape : le bouton bascule au lieu d'en créer un second. */
  const activeTimer = timers.find((timer) => timer.source === currentStep.id && timer.fired_at === null) ?? null;
  /** Avancement du minuteur de cette étape, ou `null` — voir `timers.ts`. */
  const avancementDuMinuteur = activeTimer === null ? null : avancement(activeTimer, maintenant);

  return (
    <>
      <div
        role="dialog"
        aria-label={`Mode cuisine : ${detail.title}`}
        // `top` décalé de la hauteur du bandeau de minuteurs (US-17) : sans
        // cela, le bandeau recouvrait l'en-tête du mode cuisine, donc sa
        // sortie et le réglage de taille de texte, sans autre issue (l'app
        // ne créait alors aucune entrée d'historique). Variable publiée par
        // `ActiveTimers`, à 0 px quand aucun minuteur ne tourne.
        style={{ top: 'var(--mlc-timers-height, 0px)' }}
        className="fixed inset-x-0 bottom-0 z-50 flex flex-col bg-background text-foreground"
      >
      {/* Progression toujours visible (US-08 : « étape n sur N »). */}
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-border p-4">
        <Button
          type="button"
          variant="quiet"
          size="compact"
          onClick={onExit}
        >
          <Icon name="fleche-gauche" size={0.9} /> Fiche recette
        </Button>
        {/* `order-last basis-full` sous `sm` : à 390 px, le compteur et sa
            barre réclamaient 160 px au milieu d'une rangée qui en portait
            déjà trois autres, si bien que le réglage de taille de texte
            passait seul à la ligne, décalé sous le micro. La progression
            prend désormais toute la seconde rangée, et les trois commandes
            tiennent sur la première. */}
        <div className="order-last flex basis-full flex-col items-center gap-1 sm:order-none sm:min-w-40 sm:flex-1 sm:basis-auto">
          {/* `role="status"` sur le bloc et non sur le seul compteur : au
              changement de phase, un lecteur d'écran annonce « La pâte à
              tropézienne, étape 3 sur 11 » d'un tenant. Le libellé ayant
              quitté le corps de l'étape, c'est ici qu'il doit être entendu. */}
          <div role="status" className="flex flex-col items-center gap-0.5">
            {currentPhase.label !== null && (
              <p data-testid="cook-mode-phase" className="text-label max-w-full text-center">
                {currentPhase.label}
              </p>
            )}
            <p data-testid="cook-mode-progress" className="text-data text-lg">
              Étape {stepIndex + 1} sur {steps.length}
            </p>
          </div>
          {/* Barre de progression : « étape n sur N » seul ne donne pas le
              sentiment d'avancer, qui compte quand on cuisine debout. Un
              segment par phase, large en proportion du nombre d'étapes. */}
          <div aria-hidden="true" data-testid="cook-mode-phases" className="flex w-full max-w-64 gap-1">
            {phases.map((phase) => {
              const done = Math.min(Math.max(stepIndex + 1 - phase.start, 0), phase.count);
              return (
                <div
                  key={phase.start}
                  // `bg-surface-sunken` et non `bg-surface-raised` : en thème
                  // clair, `surface-raised` vaut le blanc pur, donc **plus
                  // clair que le fond de page**. La piste non parcourue
                  // ressortait davantage que la page qui la porte, et la
                  // barre semblait pleine à l'envers. Une piste est un creux.
                  //
                  // `h-1.5` plutôt que `h-1` : à 4 px, sur une tablette posée
                  // à 60 cm au bord d'un plan de travail, la progression ne
                  // se lit pas — et c'est le seul repère d'avancement d'un
                  // écran qu'on consulte du coin de l'œil.
                  className="h-1.5 overflow-hidden rounded-pill bg-surface-sunken"
                  style={{ flexGrow: phase.count, flexBasis: 0 }}
                >
                  <div
                    className="h-full rounded-pill bg-primary transition-[width] duration-(--duration-settle) ease-entrance"
                    style={{ width: `${String((done / phase.count) * 100)}%` }}
                  />
                </div>
              );
            })}
          </div>
        </div>
        {/* Commande vocale (US-31), la seule modalité qui n'exige rien des
            mains. Absente quand l'appareil ne sait pas écouter ou que
            l'autorisation a été refusée : les autres modalités suffisent, et
            réafficher une invite serait pire que ne rien proposer. */}
        {voice.state !== 'unsupported' && voice.state !== 'denied' && (
          <Button
            variant={voice.state === 'listening' ? 'primary' : 'secondary'}
            size="icon"
            aria-pressed={voice.state === 'listening'}
            aria-label={voice.state === 'listening' ? 'Arrêter la commande vocale' : 'Commande vocale'}
            onClick={voice.toggle}
          >
            <Icon name="micro" size={1.4} />
          </Button>
        )}
        <TextSizeControl size={cookTextSize.size} increase={cookTextSize.increase} decrease={cookTextSize.decrease} />
      </header>

      {/* Ce qui est écouté, tant que le micro est ouvert (US-31). Le bouton
          seul n'apprenait pas les trois mots reconnus : il fallait deviner la
          fonction, puis deviner le vocabulaire. Affiché à l'activation plutôt
          qu'en permanence — une aide qui reste à l'écran pendant toute la
          cuisson redevient du décor. */}
      {/* **Une icône par mot** (US-59) : les trois commandes n'étaient données
          qu'en toutes lettres, et rien ne reliait le mot au geste qu'il
          déclenche — on lisait « minuteur » sans savoir si cela le lançait ou
          l'arrêtait. Le dessin est celui du bouton correspondant à l'écran.

          **Le retour « compris »** : rien ne disait qu'on avait été entendu.
          Sur une commande dont l'effet ne saute pas aux yeux — « minuteur »
          quand le bandeau est hors du champ de vision — on répétait le mot,
          ce qui n'a jamais rien amélioré. */}
      {voice.state === 'listening' && (
        <div
          data-testid="cook-mode-voice-hint"
          role="status"
          className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 border-b border-border bg-primary-soft px-4 py-2 text-center text-sm text-foreground-secondary"
        >
          {voice.derniereCommande === null ? (
            <>
              <span>À l'écoute&nbsp;:</span>
              {VOIX_LIBELLES.map(({ command, mot, icone }) => (
                <span key={command} className="inline-flex items-center gap-1">
                  <Icon name={icone} />
                  «&nbsp;{mot}&nbsp;»
                </span>
              ))}
              {/* Les commandes à argument et les deux ajouts d'US-60, en
                  texte : six pictogrammes sur une ligne redeviendraient du
                  décor, et celles-ci s'apprennent en les lisant une fois. */}
              <span className="basis-full text-xs">
                aussi «&nbsp;minuteur dix minutes&nbsp;», «&nbsp;étape trois&nbsp;»,
                «&nbsp;répète&nbsp;», «&nbsp;ingrédients&nbsp;»
              </span>
            </>
          ) : (
            <span className="inline-flex items-center gap-2 font-semibold text-foreground">
              <Icon name={VOIX_PAR_COMMANDE[voice.derniereCommande].icone} />
              «&nbsp;{VOIX_PAR_COMMANDE[voice.derniereCommande].mot}&nbsp;» — compris
            </span>
          )}
        </div>
      )}

      {/* Rappel « la dernière fois » (US-11), dans le flux depuis US-58 : il
          se lisait auparavant sur une fenêtre pleine page qu'il fallait
          fermer avant d'atteindre la première étape. */}
      {reminder}

      {/* `key={currentStep.id}` (I5, refonte UX/UI passe 4) : force le
          remontage de tout le bloc à chaque changement d'étape, ce qui
          rejoue l'animation d'apparition (`.animate-fade-in`, index.css) —
          seul moyen simple d'obtenir une transition sur un changement de
          contenu avec React/CSS, sans bibliothèque d'animation dédiée. */}
      {/* Balayage horizontal (US-31), troisième modalité après le clavier et
          les zones de frappe. Posé sur le contenu et non sur le dialogue
          entier : le bandeau de minuteurs et le pied de page ont leurs
          propres gestes. */}
      <main
        key={currentStep.id}
        onTouchStart={(e) => { swipeStartX.current = e.touches[0]?.clientX ?? null; }}
        onTouchEnd={(e) => {
          const start = swipeStartX.current;
          swipeStartX.current = null;
          if (start === null) return;
          const end = e.changedTouches[0]?.clientX ?? start;
          const distance = end - start;
          // 60 px : au-delà d'un tremblement de doigt, en deçà d'un geste
          // qu'on hésiterait à faire d'une main occupée.
          if (Math.abs(distance) < SWIPE_MIN_PX) return;
          if (distance < 0) goNext();
          else goPrevious();
        }}
        className="flex flex-1 flex-col overflow-y-auto p-6 animate-fade-in"
      >
        {/* Conteneur interne centré verticalement via une marge automatique
            (I2, refonte UX/UI passe 5) plutôt qu'un `justify-content: center`
            sur ce <main> défilant : une étape longue reste entièrement
            accessible par défilement au lieu d'être tronquée en haut. */}
        {/*
          **Deux colonnes dès `md`** (refonte éditoriale) : l'étape à gauche,
          les ingrédients qu'elle demande à droite.

          Le constat, mesuré sur une tablette de 834 × 1112 — le format que le
          brief déclare prioritaire, et celui de l'appareil réellement posé sur
          le plan de travail : une étape de deux lignes occupait **15 % de la
          hauteur**, centrée dans un aplat vide. Les ingrédients de l'étape
          existaient pourtant déjà, empilés *sous* le texte, donc hors du champ
          de vision dès que l'étape était longue ; et le tiroir « Tous les
          ingrédients » demandait un geste pour montrer ce que l'écran avait la
          place d'afficher en permanence.

          Aucune donnée nouvelle : `step_ingredient` (§2 CLAUDE.md) apparie
          déjà les ingrédients à leur étape, et cette liste était rendue au
          même endroit du composant. Elle change de place, pas de contenu.

          La grille ne se dédouble que s'il y a quelque chose à mettre à
          droite : une étape sans ingrédient apparié garde toute la largeur,
          sinon elle se retrouverait tassée dans une colonne au profit d'un
          vide. Sur téléphone, rien ne change — une colonne, dans l'ordre
          d'origine.
        */}
        <div className={`mx-auto my-auto grid w-full gap-6 ${
          stepIngredients.length > 0
            ? 'max-w-reading md:max-w-5xl md:grid-cols-[minmax(0,1fr)_17rem] md:items-start md:gap-10'
            : 'max-w-reading'
        }`}
        >
        {/*
          **Le numéro d'étape, dans le corps de l'écran** (refonte éditoriale).

          Il n'existait qu'en haut, dans « Étape 3 sur 27 », en 18 px, à
          l'autre bout d'un écran de tablette. Le corps de la page ne portait
          donc qu'une phrase flottant au milieu d'un aplat : rien ne disait
          où l'on se trouvait sans lever les yeux, et rien ne donnait à cet
          écran — le plus utilisé de l'application, celui qu'on regarde les
          mains dans la pâte — la moindre identité.

          Un chiffre en Fraunces, à l'axe optique bas pour tenir le trait, sur
          la même ligne de base que le texte. Il est `aria-hidden` : la
          progression est déjà annoncée par le `role="status"` de l'en-tête,
          et l'entendre deux fois à chaque étape serait du bruit.

          Sa taille suit le réglage 20→32 px (§4 CLAUDE.md) comme le reste :
          agrandir le texte sans agrandir son repère les désaccorderait.
        */}
        <div className="flex items-start gap-3">
          {/* Case du design system depuis US-25 : la native mesurait 13 px
              de large sur l'écran que §4 veut utilisable les mains grasses. */}
          <Checkbox
            size="cook"
            label="Étape terminée"
            checked={checkedStepIds.has(currentStep.id)}
            onChange={() => { setCheckedStepIds((current) => toggle(current, currentStep.id)); }}
          />
          <span
            aria-hidden="true"
            data-testid="cook-mode-numero"
            className="text-numeral shrink-0 select-none pt-1 text-foreground-muted"
            style={{ fontSize: cookTextSize.size * 1.6 }}
          >
            {stepIndex + 1}
          </span>
          <div className="flex flex-col gap-2">
            {/* Corps de texte du mode cuisine à 20 px minimum, réglable
                jusqu'à 32 px (§4 CLAUDE.md, TextSizeControl ci-dessus).
                Estompé une fois cochée (I5) : confirmation visuelle du
                cochage, en plus de la case elle-même. */}
            <div style={{ fontSize: cookTextSize.size }}>
              {/* Ingrédients, durées et températures surlignés dans le texte
                  (US-39). Toucher une durée arme le minuteur de l'étape : le
                  geste se fait là où la durée est écrite, sans chercher le
                  bouton en bas d'écran. */}
              <StepText
                instruction={currentStep.instruction}
                ingredients={stepIngredients
                  .map((ingredient) => ingredient.name_normalized ?? '')
                  .filter((nom) => nom.length > 0)}
                onDuree={(secondes) => { startTimer(timerLabel, secondes, currentStep.id); }}
                // `leading-relaxed` et non `leading-snug` : une étape se lit
                // debout, à distance, souvent en diagonale — c'est
                // l'interlignage, plus que le corps, qui décide si l'œil
                // retrouve la ligne suivante après avoir regardé la casserole.
                className={`leading-relaxed transition-opacity duration-(--duration-quick) ease-entrance ${checkedStepIds.has(currentStep.id) ? 'opacity-60' : ''}`}
              />
            </div>
            {(dureeSecondes !== null || currentStep.temperature_c !== null) && (
              <p style={{ fontSize: cookTextSize.size * 0.75 }} className="text-data flex flex-wrap items-center gap-2 text-foreground-secondary">
                {dureeSecondes !== null ? formatDureeSecondes(dureeSecondes) ?? '' : ''}
                {dureeSecondes !== null && currentStep.temperature_c !== null ? ' · ' : ''}
                {currentStep.temperature_c !== null ? formatTemperatureDEtape(currentStep.temperature_c, currentStep.instruction) ?? '' : ''}
              </p>
            )}
            {/* Bouton de lancement pré-rempli (US-09) : n'apparaît que si
                l'étape porte une durée. Le minuteur démarre immédiatement
                pour cette durée — pas de champ à ajuster avant de lancer,
                cohérent avec « aucun geste précis » du mode cuisine (§4). */}
            {dureeSecondes !== null && activeTimer === null && (
              <button
                type="button"
                onClick={() => { startTimer(timerLabel, dureeSecondes, currentStep.id); }}
                className={buttonClasses('secondary', 'large', 'w-fit')}
              >
                {/* Le libellé disait « ({durationMin} min) », un nombre nu
                    suivi d'une unité en dur : sur « 20 sec » il aurait dit
                    « 20 min ». Le formateur porte l'unité, comme partout
                    ailleurs (§3.5 : aucune valeur brute à l'écran). */}
                Lancer un minuteur ({formatDureeSecondes(dureeSecondes) ?? ''})
              </button>
            )}
            {/*
              **Le décompte est là où l'on vient d'appuyer** (US-102).

              Ici s'affichait « Minuteur en cours pour cette étape. » — une
              phrase grise, sans un chiffre. Le bouton venait de disparaître,
              et la seule trace du geste était cette ligne : pour savoir
              combien de temps il restait, il fallait lever les yeux vers le
              bandeau, tout en haut de l'écran, où le décompte était de toute
              façon poussé hors du cadre.

              Le bandeau reste — il suit d'un écran à l'autre, c'est son
              rôle. Mais l'étape en cours porte désormais son propre
              décompte, en grand, à la place exacte du bouton qu'on a touché,
              avec de quoi l'arrêter sans remonter.
            */}
            {activeTimer !== null && (
              <div className="flex w-fit flex-col gap-2 rounded-card border border-border bg-surface-raised p-3">
                <div className="flex items-center gap-4">
                  <span
                    className="text-data text-4xl leading-none text-primary"
                    aria-label={`Temps restant : ${formatRemaining(activeTimer.end_at - maintenant)}`}
                  >
                    {formatRemaining(activeTimer.end_at - maintenant)}
                  </span>
                  <Button
                    type="button"
                    variant="secondary"
                    size="large"
                    onClick={() => { dismissTimer(activeTimer.id); }}
                  >
                    Arrêter
                  </Button>
                </div>
                {avancementDuMinuteur !== null && (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken" role="presentation">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-linear"
                      style={{ width: `${String(Math.round(avancementDuMinuteur * 100))}%` }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {stepIngredients.length > 0 && (
          // Un filet vertical sépare les deux colonnes, plutôt qu'une carte :
          // encadrer la liste en ferait un panneau flottant au milieu de
          // l'écran, là où un trait dit simplement « ceci accompagne cela ».
          // Sous `md`, le filet passe à l'horizontale, au-dessus de la liste.
          <section
            aria-label="Ingrédients de l’étape"
            className="flex flex-col gap-3 border-t border-border pt-5 md:border-t-0 md:border-l md:pl-10 md:pt-0"
          >
            {/* Le mode cuisine n'avait aucun titre de section — §4 CLAUDE.md
                demande un plan de titres par écran. Il en a un désormais, et
                il sert aussi à dire que cette liste-là est celle de l'étape en
                cours, pas celle de la recette. */}
            <h2 className="text-label" style={{ fontSize: Math.max(12, cookTextSize.size * 0.55) }}>
              Pour cette étape
            </h2>
          <ul className="flex flex-col gap-3">
            {stepIngredients.map((ingredient) => {
              const adapted = adaptedById.get(ingredient.id);
              const checked = checkedIngredientIds.has(ingredient.id);
              // Déjà coché à une étape précédente (US-17) : l'ingrédient
              // réapparaissait vierge d'une étape à l'autre, si bien qu'on
              // cochait deux fois le même oignon (audit externe, constat 31).
              const alreadyPrepared = checked && !currentStepFirstUse.has(ingredient.id);
              return (
                <li key={ingredient.id} className="flex items-center gap-3">
                  <Checkbox
                    size="cook"
                    label={`Ingrédient : ${ingredient.name}`}
                    checked={checked}
                    onChange={() => { setCheckedIngredientIds((current) => toggle(current, ingredient.id)); }}
                  />
                  {/* Barré une fois coché (I5, même principe que l'étape ci-dessus) : confirmation visuelle du cochage. */}
                  <span
                    style={{ fontSize: cookTextSize.size * 0.85 }}
                    className={`transition-opacity duration-(--duration-quick) ease-entrance ${checked ? 'opacity-60 line-through' : ''}`}
                  >
                    {adapted?.display !== null && adapted?.display !== undefined && (
                      <span className="text-data">
                        {joinQuantityUnit(adapted.display, ingredient.unit, adapted.quantity)}{' '}
                      </span>
                    )}
                    {ingredient.name}
                    {ingredient.note !== null && <span className="text-foreground-secondary"> ({ingredient.note})</span>}
                    {alreadyPrepared && (
                      <span className="text-foreground-muted"> — déjà préparé</span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>

          {/*
            **La recette entière, sous les ingrédients de l'étape** — et
            seulement à partir de `md`.

            Le tiroir du bas fait doublon dès que la colonne existe… mais
            **un doublon partiel** : la colonne ne montre que les ingrédients
            appariés à l'étape en cours, le tiroir montre les quatorze de la
            recette. Le masquer sans plus aurait retiré à la tablette — le
            format réellement posé sur le plan de travail — l'accès à la
            liste complète, et laissé sans effet la commande vocale
            « ingrédients » (US-60) sur ce même format. Un geste qui ne fait
            rien est pire que pas de geste.

            La liste complète monte donc ici, en permanence : sur un écran de
            834 px de large, la place existe, et une liste toujours visible
            vaut mieux qu'un tiroir à ouvrir les mains grasses. La commande
            vocale n'ouvre plus rien sur ce format parce qu'il n'y a plus
            rien à ouvrir — son intention est déjà satisfaite.

            **C'est le seul endroit du dépôt où un contenu est écrit deux
            fois** (ici et dans le tiroir). Les deux sont exclusifs par
            `display`, donc un seul existe à la fois dans l'arbre
            d'accessibilité. Le monter une seule fois aurait demandé de le
            déplacer par le CSS, comme la navigation d'US-56 ; impossible
            ici, l'animation d'apparition de l'étape pose un `transform` sur
            l'ancêtre, ce qui capturerait le positionnement `fixed` du tiroir
            et le ferait sauter à chaque changement d'étape.
          */}
          {detail.ingredients.length > autresIngredients.length && autresIngredients.length > 0 && (
            <div className="hidden flex-col gap-3 border-t border-border pt-5 md:flex">
              <h3 className="text-label" style={{ fontSize: Math.max(11, cookTextSize.size * 0.5) }}>
                Le reste de la recette
              </h3>
              <ul className="flex flex-col gap-2">
                {autresIngredients.map((ingredient) => {
                  const adapted = adaptedById.get(ingredient.id);
                  const utilise = checkedIngredientIds.has(ingredient.id);
                  return (
                    <li
                      key={ingredient.id}
                      style={{ fontSize: cookTextSize.size * 0.7 }}
                      className={`text-foreground-secondary ${utilise ? 'opacity-60 line-through' : ''}`}
                    >
                      {adapted?.display !== null && adapted?.display !== undefined && (
                        <span className="text-data">
                          {joinQuantityUnit(adapted.display, ingredient.unit, adapted.quantity)}{' '}
                        </span>
                      )}
                      {ingredient.name}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          </section>
        )}
        </div>
      </main>

      {/* Tiroir d'ingrédients, tiré depuis le bas (US-40, livré en US-58).

          Placé entre le contenu et les zones de frappe : c'est le bord que la
          main atteint sans déplacer le regard, et il ne recouvre jamais
          « Précédent / Suivant ». Fermé, il ne coûte qu'une ligne ; ouvert, il
          défile sur au plus 40 % de la hauteur, de sorte que l'étape en cours
          reste lisible derrière lui — un tiroir n'est pas une fenêtre.

          `hidden` plutôt qu'un démontage : le contenu reste dans le document,
          donc trouvable par la recherche du navigateur, et l'ouverture ne
          reconstruit pas la liste. */}
      {/* `md:hidden` : sur tablette et bureau, la colonne de droite porte
          désormais l'étape **et** le reste de la recette. */}
      {detail.ingredients.length > 0 && (
        <div className="border-t border-border bg-surface md:hidden">
          <button
            type="button"
            aria-expanded={tiroirOuvert}
            aria-controls="tiroir-ingredients"
            onClick={() => { setTiroirOuvert((ouvert) => !ouvert); }}
            // Classes explicites plutôt que `buttonClasses` : la primitive
            // centre son contenu (`justify-center`), et cette poignée pleine
            // largeur veut un libellé à gauche, l'indicateur à droite.
            className={`flex min-h-touch w-full items-center justify-between px-4 text-sm text-foreground-secondary transition hover:text-primary ${FOCUS_RING}`}
          >
            <span>Tous les ingrédients ({detail.ingredients.length})</span>
            <Icon name={tiroirOuvert ? 'moins' : 'plus'} />
          </button>
          {/* `.disclosure` plutôt que `hidden` : un tiroir qui apparaît d'un
              bloc sous les doigts, dans une cuisine, se lit comme un
              changement d'écran. Le `overflow-y-auto` descend d'un cran —
              l'enfant direct de `.disclosure` porte déjà `overflow: hidden`
              pour révéler le contenu progressivement, et deux règles de
              débordement sur le même élément se contredisent. */}
          <div id="tiroir-ingredients" data-ouvert={tiroirOuvert} className="disclosure">
            <div className="max-h-[40vh] overflow-y-auto px-4 pb-4">
            <ul aria-label="Tous les ingrédients" className="mx-auto flex w-full max-w-reading flex-col gap-2">
              {detail.ingredients.map((ingredient) => {
                const adapted = adaptedById.get(ingredient.id);
                // Coché à l'étape où il servait (US-17) : le tiroir montre le
                // même état que la liste de l'étape, sinon il raconte une
                // autre histoire que l'écran qu'il complète.
                const utilise = checkedIngredientIds.has(ingredient.id);
                return (
                  <li
                    key={ingredient.id}
                    style={{ fontSize: cookTextSize.size * 0.7 }}
                    className={utilise ? 'opacity-60 line-through' : ''}
                  >
                    {adapted?.display !== null && adapted?.display !== undefined && (
                      <span className="text-data">
                        {joinQuantityUnit(adapted.display, ingredient.unit, adapted.quantity)}{' '}
                      </span>
                    )}
                    {ingredient.name}
                  </li>
                );
              })}
            </ul>
            </div>
          </div>
        </div>
      )}

      {/* Zones de frappe géantes, moitié gauche / moitié droite (US-31) : les
          boutons occupaient une fraction de la largeur, ce qui demandait de
          viser. Ici, la moitié de l'écran sur 88 px de haut — on avance du
          dos de la main. */}
      <footer className="flex items-stretch gap-3 border-t border-border p-3">
        <button
          type="button"
          disabled={isFirst}
          onClick={goPrevious}
          className={buttonClasses('secondary', 'large', 'flex-1 min-h-22')}
        >
          Précédent
        </button>
        <button
          type="button"
          onClick={goNext}
          className={buttonClasses('primary', 'large', 'flex-1 min-h-22')}
        >
          {isLast ? 'Terminer' : 'Suivant'}
        </button>
      </footer>
      </div>
    </>
  );
}
